import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Building2, ExternalLink, Bot, UserCog, History, DollarSign } from "lucide-react";
import type { UpworkAssignment, AssignmentStatus, AssignmentHistoryEntry } from "../api/UpworkJobsApi";
import { STATUS_OPTIONS, TERMINAL, fetchAssignmentHistory } from "../api/UpworkJobsApi";
import HighlightedText from "shared/HighlightedText";
import StatusNotePopover from "shared/StatusNotePopover";
import ReassignPopover from "shared/ReassignPopover";

const STATUS_CHIP: Record<string, { label: string; className: string }> = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, { label: o.label, className: TERMINAL.has(o.value) ? "bg-muted text-muted-foreground border-border" : "bg-primary text-primary-foreground border-primary/70" }])
);
STATUS_CHIP.hired = { label: "Hired", className: "bg-emerald-500 text-emerald-950 border-emerald-400" };
STATUS_CHIP.rejected = { label: "Rejected", className: "bg-red-500 text-white border-red-400" };

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
    " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
}

function clientName(clientInfoJson: string | null | undefined): string {
  if (!clientInfoJson) return "Client";
  try {
    const parsed = JSON.parse(clientInfoJson);
    return parsed.companyName || "Client";
  } catch {
    return "Client";
  }
}

interface Props {
  assignment: UpworkAssignment;
  isAdmin: boolean;
  techManagers: { id: number; name: string }[];
  onClose: () => void;
  onStatusUpdate: (assignmentId: number, status: AssignmentStatus, notes: string | null) => Promise<void>;
  onReassign?: (assignmentId: number, employeeId: number) => Promise<void>;
}

export default function AssignmentDetailModal({ assignment, isAdmin, techManagers, onClose, onStatusUpdate, onReassign }: Props) {
  const techManager = isAdmin && assignment.employee_id ? techManagers.find((tm) => tm.id === assignment.employee_id) ?? null : null;

  const [history, setHistory] = useState<AssignmentHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const historyBtnRef = useRef<HTMLButtonElement>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyRect, setHistoryRect] = useState<DOMRect | null>(null);

  const [localStatus, setLocalStatus] = useState<AssignmentStatus>(assignment.status);
  const [pendingStatus, setPendingStatus] = useState<AssignmentStatus | null>(null);
  const [pendingNotes, setPendingNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isTerminal = TERMINAL.has(localStatus);

  useEffect(() => {
    setHistoryLoading(true);
    fetchAssignmentHistory(assignment.assignment_id, isAdmin)
      .then(setHistory)
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [assignment.assignment_id, isAdmin]);

  function selectStatus(status: AssignmentStatus) {
    if (isTerminal || status === localStatus) {
      setPendingStatus(null);
      setPendingNotes("");
      setSaveError(null);
      return;
    }
    setPendingStatus(status);
    setPendingNotes("");
    setSaveError(null);
  }

  async function confirmStatusChange() {
    if (!pendingStatus) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onStatusUpdate(assignment.assignment_id, pendingStatus, pendingNotes.trim() || null);
      setLocalStatus(pendingStatus);
      setPendingStatus(null);
      setPendingNotes("");
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative w-full max-w-2xl max-h-[90vh] bg-surface border border-border rounded-2xl flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4 p-6 border-b border-border shrink-0">
          <div className="w-12 h-12 rounded-xl border border-border bg-surface-muted flex items-center justify-center shrink-0">
            <span className="text-lg font-bold text-primary font-inter">{assignment.title.charAt(0).toUpperCase()}</span>
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground font-inter leading-snug">{assignment.title}</h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              <span className="flex items-center gap-1 text-xs text-muted-foreground font-inter">
                <Building2 className="w-3 h-3" />
                {clientName(assignment.client_info)}
              </span>
              {assignment.amount_display_value && (
                <span className="flex items-center gap-1 text-xs text-emerald-400 font-inter">
                  <DollarSign className="w-3 h-3" />
                  {assignment.amount_display_value}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <span className="text-[11px] px-2 py-1 rounded-lg border bg-primary/10 text-primary border-primary/20 font-inter">
                {assignment.search_profile}
              </span>
              {assignment.experience_level && (
                <span className="text-[11px] px-2 py-1 rounded-lg border bg-muted/50 text-muted-foreground border-border font-inter">
                  {assignment.experience_level}
                </span>
              )}
              <span
                className={`flex items-center gap-0.5 text-[11px] px-2 py-1 rounded-lg border font-inter ${
                  assignment.assigned_by === "auto"
                    ? "bg-violet-500/10 text-violet-400 border-violet-500/20"
                    : "bg-muted/50 text-muted-foreground border-border"
                }`}
              >
                {assignment.assigned_by === "auto" ? <Bot className="w-3 h-3" /> : <UserCog className="w-3 h-3" />}
                {assignment.assigned_by === "auto" ? "Auto-assigned" : "Manually assigned"}
              </span>
              {isAdmin && techManager && (
                <div className="ml-auto">
                  {onReassign ? (
                    <ReassignPopover
                      candidates={techManagers}
                      currentEmployeeId={assignment.employee_id ?? null}
                      onReassign={(employeeId) => onReassign(assignment.assignment_id, employeeId)}
                    >
                      <span className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border bg-muted/50 text-muted-foreground border-border font-inter">
                        <UserCog className="w-3 h-3" />
                        {techManager.name}
                      </span>
                    </ReassignPopover>
                  ) : (
                    <span className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border bg-muted/50 text-muted-foreground border-border font-inter">
                      {techManager.name}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors shrink-0 self-start">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Status tracking */}
        <div className="px-6 pt-3 pb-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground font-inter">Tracking</span>
            <button
              ref={historyBtnRef}
              onClick={() => {
                if (historyBtnRef.current) setHistoryRect(historyBtnRef.current.getBoundingClientRect());
                setShowHistory((v) => !v);
              }}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary font-inter transition-colors underline underline-offset-2"
            >
              <History className="w-3 h-3" />
              History
            </button>
          </div>

          {isTerminal ? (
            <div className="flex items-center gap-2">
              <StatusNotePopover
                note={assignment.assignment_notes}
                fromLabel={assignment.assignment_from_status ? STATUS_CHIP[assignment.assignment_from_status]?.label : null}
                toLabel={STATUS_CHIP[localStatus]?.label ?? localStatus}
              >
                <span className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter font-medium ${STATUS_CHIP[localStatus]?.className ?? "bg-muted text-muted-foreground border-border"} ${assignment.assignment_notes ? "cursor-help" : ""}`}>
                  {STATUS_CHIP[localStatus]?.label ?? localStatus}
                </span>
              </StatusNotePopover>
              <p className="text-xs text-muted-foreground font-inter italic">This assignment is in a terminal state and cannot be updated.</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((opt) => {
                const isCurrent = localStatus === opt.value;
                const isPending = pendingStatus === opt.value;
                return (
                  <StatusNotePopover
                    key={opt.value}
                    note={isCurrent && !isPending ? assignment.assignment_notes : null}
                    fromLabel={assignment.assignment_from_status ? STATUS_CHIP[assignment.assignment_from_status]?.label : null}
                    toLabel={opt.label}
                  >
                    <button
                      onClick={() => selectStatus(opt.value)}
                      className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter font-medium transition-all ${
                        isPending
                          ? `${STATUS_CHIP[opt.value]?.className ?? "bg-muted text-muted-foreground border-border"} ring-2 ring-white/20 ring-offset-1 ring-offset-surface`
                          : isCurrent
                          ? STATUS_CHIP[opt.value]?.className ?? "bg-muted text-muted-foreground border-border"
                          : "bg-transparent text-muted-foreground border-border hover:border-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {opt.label}
                    </button>
                  </StatusNotePopover>
                );
              })}
            </div>
          )}

          {pendingStatus && !isTerminal && (
            <div className="mt-3 space-y-2">
              <textarea
                value={pendingNotes}
                onChange={(e) => setPendingNotes(e.target.value)}
                placeholder="Add a note for this status change… (optional)"
                rows={2}
                autoFocus
                className="w-full bg-surface-muted/50 border border-border rounded-lg px-3 py-2 text-xs text-foreground placeholder-muted-foreground font-inter resize-none focus:outline-none focus:border-primary/60 transition-colors"
              />
              {saveError && <p className="text-[11px] text-destructive font-inter">{saveError}</p>}
              <div className="flex items-center gap-2">
                <button
                  onClick={confirmStatusChange}
                  disabled={saving}
                  className="px-3 py-1.5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-xs font-inter font-medium rounded-lg transition-colors"
                >
                  {saving ? "Saving…" : `Save as "${STATUS_CHIP[pendingStatus]?.label ?? pendingStatus}"`}
                </button>
                <button
                  onClick={() => { setPendingStatus(null); setPendingNotes(""); setSaveError(null); }}
                  className="px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground text-xs font-inter rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* History popover */}
        {showHistory && historyRect &&
          createPortal(
            <>
              <div className="fixed inset-0 z-[80]" onClick={() => setShowHistory(false)} />
              <div
                className="fixed z-[81] w-80 bg-surface-muted border border-border rounded-xl shadow-2xl overflow-hidden"
                style={{ top: historyRect.bottom + 6, left: historyRect.left }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <span className="text-xs font-semibold text-foreground font-inter">Status History</span>
                  <button onClick={() => setShowHistory(false)} className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="px-4 py-3 max-h-72 overflow-y-auto">
                  {historyLoading ? (
                    <p className="text-xs text-muted-foreground font-inter py-1">Loading…</p>
                  ) : history.length === 0 ? (
                    <p className="text-xs text-muted-foreground font-inter italic py-1">No status changes yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {history.map((h, i) => (
                        <div key={h.id} className="relative pl-4">
                          {i < history.length - 1 && <span className="absolute left-[3px] top-3 bottom-[-8px] w-px bg-border" />}
                          <span className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-primary ring-2 ring-surface-muted" />
                          <div className="flex flex-wrap items-center gap-1.5">
                            {h.from_status && (
                              <>
                                <span className={`px-1.5 py-0.5 rounded border text-[10px] font-inter ${STATUS_CHIP[h.from_status]?.className ?? "text-muted-foreground border-transparent"}`}>
                                  {STATUS_CHIP[h.from_status]?.label ?? h.from_status}
                                </span>
                                <span className="text-muted-foreground text-[10px]">→</span>
                              </>
                            )}
                            <span className={`px-1.5 py-0.5 rounded border text-[10px] font-inter ${STATUS_CHIP[h.to_status]?.className ?? "text-muted-foreground border-transparent"}`}>
                              {STATUS_CHIP[h.to_status]?.label ?? h.to_status}
                            </span>
                            <span className="ml-auto text-[10px] text-muted-foreground font-inter shrink-0">{formatDate(h.changed_at)}</span>
                          </div>
                          {h.notes && <p className="text-[11px] text-muted-foreground font-inter mt-0.5 italic">"{h.notes}"</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>,
            document.body,
          )}

        {/* Description */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {assignment.description ? (
            <div className="text-sm text-foreground font-inter leading-relaxed whitespace-pre-wrap">
              <HighlightedText text={assignment.description} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground font-inter italic">
              No description available — view it on Upwork.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border shrink-0">
          <a
            href={assignment.job_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-inter font-medium rounded-xl transition-colors whitespace-nowrap"
          >
            <ExternalLink className="w-4 h-4" />
            Apply Manually on Upwork
          </a>
        </div>
      </div>
    </div>
  );
}
